<script setup lang="ts">
    import {
        abortPublishingRelease,
        adminPageArchiveJob,
        adminPageJobs,
        adminPageLateSchedules,
        adminPageReleases,
        adminPageRestartJob,
        adminPageScheduleOutput,
        adminPageSchedules,
        adminPageSchedulesSignificantDeltaTimeChange,
        discardRelease,
        getScheduleExecutions,
        LateSchedulesDTO,
        JobIssueDTO,
        ReleaseDTO,
        runScheduleById,
        ScheduleDeltaTimeDTO,
        ScheduleDTO,
        ScheduleExecution,
        ScheduleExecutionOutput
    } from '@/shared/helpers/api';
    import { getCodeSetLink, getLibraryLink, getReleaseLink, getSubscriptionLink } from '@/shared/helpers/routing';
    import { computed, onMounted, ref } from 'vue';
    import { http } from '@/shared/httpWrapper';
    import useShow from '@/shared/composables/useShow';
    import { longDate } from '@/formatters';
    import moment from 'moment';

    type ReleaseViewModel = ReleaseDTO & {
        isAborting?: boolean;
        isDiscarding?: boolean;
        abortPublishingError?: string;
    };

    const { showError, showInfo } = useShow();

    const jobs = ref<JobIssueDTO[]>([]);
    const schedules = ref<ScheduleDTO[]>([]);
    const schedulesDelta = ref<ScheduleDeltaTimeDTO[]>([]);
    const releases = ref<ReleaseViewModel[]>([]);
    const lateSchedules = ref<LateSchedulesDTO[]>([]);
    const scheduleRuns = ref<ScheduleExecution[]>([]);

    const jobsLoading = ref<boolean>(false);
    const schedulesLoading = ref<boolean>(false);
    const schedulesDeltaLoading = ref<boolean>(false);
    const releasesLoading = ref<boolean>(false);
    const lateSchedulesLoading = ref<boolean>(false);
    const outputData = ref<ScheduleExecutionOutput>({ output: '' });
    const showOutput = ref<boolean>(false);

    const runsModalActive = computed(() => {
        return scheduleRuns.value.length > 0;
    });

    const showReleaseActionColumn = computed(() => {
        return releases.value.some(release => release.state === 'Publishing' || release.state === 'Open');
    });

    onMounted(() => {
        refreshAll();
    });

    async function abortPublishing(release: ReleaseViewModel): Promise<void> {
        release.isAborting = true;
        release.abortPublishingError = '';

        try {
            await abortPublishingRelease(http, release.id);
            showInfo('Publishing aborted.');
            await loadJobs();
        } catch (ex) {
            showError(ex, (errorMessage: string) => {
                release.abortPublishingError = errorMessage;
            });
        }

        release.isAborting = false;
        await loadReleases();
        await loadJobs();
    }

    async function discardReleaseAction(release: ReleaseViewModel): Promise<void> {
        release.isDiscarding = true;
        release.abortPublishingError = '';

        try {
            await discardRelease(http, release.id);
            showInfo('Release discarded.');
            await loadJobs();
            await loadReleases();
        } catch (ex) {
            showError(ex);
        }

        release.isDiscarding = false;
    }

    function refreshAll(): void {
        void loadJobs();
        void loadSchedules();
        void loadSchedulesSignificantDeltaTimeChange();
        void loadReleases();
        void loadLateSchedules();
    }

    async function loadJobs(): Promise<void> {
        jobsLoading.value = true;
        jobs.value = await adminPageJobs(http);
        jobsLoading.value = false;
    }

    async function loadSchedules(): Promise<void> {
        schedulesLoading.value = true;
        schedules.value = await adminPageSchedules(http);
        schedulesLoading.value = false;
    }

    async function loadSchedulesSignificantDeltaTimeChange(): Promise<void> {
        schedulesDeltaLoading.value = true;
        schedulesDelta.value = await adminPageSchedulesSignificantDeltaTimeChange(http);
        schedulesDeltaLoading.value = false;
    }

    async function loadReleases(): Promise<void> {
        releasesLoading.value = true;
        releases.value = await adminPageReleases(http);
        releasesLoading.value = false;
    }

    async function loadLateSchedules(): Promise<void> {
        lateSchedulesLoading.value = true;
        lateSchedules.value = await adminPageLateSchedules(http);
        lateSchedulesLoading.value = false;
    }

    async function openOutput(id: number): Promise<void> {
        outputData.value.output = '';
        outputData.value = await adminPageScheduleOutput(http, id);
        showOutput.value = true;
    }

    async function openRuns(scheduleId: number): Promise<void> {
        scheduleRuns.value = await getScheduleExecutions(http, scheduleId);
    }

    function closeRuns(): void {
        scheduleRuns.value = [];
    }

    async function selectRun(run: ScheduleExecution): Promise<void> {
        await openOutput(run.id);
        closeRuns();
    }

    async function runSchedule(id: number): Promise<void> {
        await runScheduleById(http, id);
        showInfo('Schedule started');
        await loadSchedules();
    }

    async function archiveJob(id: number): Promise<void> {
        await adminPageArchiveJob(http, id);
        showInfo('Job archived');
        await loadJobs();
    }

    async function rerunJob(id: number): Promise<void> {
        await adminPageRestartJob(http, id);
        showInfo('Job re-inserted');
        await loadJobs();
    }

    function humanizeSeconds(s: number): string {
        return `${moment.duration(s, 'seconds').humanize()} (${s} seconds)`;
    }
</script>

<template>
    <div>
        <b-modal
            :model-value="runsModalActive"
            has-modal-card
            @close="closeRuns">
            <div
                class="modal-card"
                style="height: auto; width: auto;">
                <header class="modal-card-head">
                    <p class="modal-card-title">
                        Select run..
                    </p>
                </header>
                <section class="modal-card-body">
                    <div
                        style="height: auto; width: auto;">
                        <b-table
                            :data="scheduleRuns"
                            default-sort="startDate"
                            default-sort-direction="desc"
                            class="cursorPointer"
                            :striped="true"
                            :narrowed="true"
                            :hoverable="true"
                            @click="selectRun">
                            <b-table-column
                                v-slot="props"
                                sortable
                                field="id"
                                label="ID">
                                {{ props.row.id }}
                            </b-table-column>
                            <b-table-column
                                v-slot="props"
                                field="action"
                                label="State">
                                <b-icon
                                    v-if="props.row.failed"
                                    pack="mdi"
                                    size="is-small"
                                    icon="close-circle"
                                    style="color:red;" />
                                <b-icon
                                    v-else-if="props.row.running"
                                    pack="mdi"
                                    size="is-small"
                                    icon="loading"
                                    custom-class="mdi-spin"
                                    style="color:blue;" />
                            </b-table-column>
                            <b-table-column
                                v-slot="props"
                                sortable
                                field="startDate"
                                label="Start">
                                {{ longDate(props.row.startDate) }}
                            </b-table-column>
                            <b-table-column
                                v-slot="props"
                                sortable
                                field="endDate"
                                label="End">
                                {{ longDate(props.row.endDate) }}
                            </b-table-column>
                        </b-table>
                    </div>
                </section>
            </div>
        </b-modal>

        <div class="columns">
            <div class="column is-2">
                <h1 class="title">
                    <b-icon
                        pack="mdi"
                        icon="cog"
                        size="" />
                    Monitoring
                </h1>
            </div>
            <div class="column">
                <div
                    class="field is-grouped is-pulled-right"
                    style="margin-top:10px;">
                    <div class="control buttons has-addons">
                        <b-button
                            @click="refreshAll">
                            <b-icon
                                pack="mdi"
                                icon="refresh"
                                size="is-small" />
                            <span>Refresh</span>
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="block">
            <h4 class="title is-4">
                Schedules (running/failed)
            </h4>

            <div v-if="showOutput">
                <pre style="padding: 6px;background-color: black;color: lightgreen"><b-button
                    type="is-dark"
                class="is-pulled-right"
                    @click="showOutput = false">
                    <b-icon
                        size="is-small"
                        pack="mdi"
                        icon="close-circle" />
                </b-button>{{ outputData.output }}</pre>
            </div>

            <b-table
                :data="schedules"
                :striped="true"
                :narrowed="true"
                :loading="schedulesLoading"
                :hoverable="true">
                <b-table-column
                    v-slot="props"
                    field="description"
                    label="Schedule">
                    {{ props.row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="action"
                    label="Action">
                    {{ props.row.action }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="action"
                    label="State">
                    <b-icon
                        v-if="props.row.failed"
                        size="is-small"
                        pack="mdi"
                        icon="close-circle"
                        style="color:red;" />
                    <b-icon
                        v-else-if="props.row.running"
                        size="is-small"
                        pack="mdi"
                        icon="loading"
                        custom-class="mdi-spin"
                        style="color:blue;" />
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="startDate"
                    label="Start">
                    {{ longDate(props.row.startDate) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="endDate"
                    label="End">
                    {{ longDate(props.row.endDate) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    numeric
                    field="id"
                    label="Inspect">
                    <div class="buttons is-justify-content-end">
                        <b-button
                            type="is-primary"
                            size="is-small"
                            @click="runSchedule(props.row.scheduleId)">
                            <b-icon
                                pack="mdi"
                                icon="play"
                                size="is-small"
                                aria-hidden="true" />
                            <span>Run NOW!</span>
                        </b-button>
                        <b-button
                            type="is-info"
                            size="is-small"
                            @click="openRuns(props.row.scheduleId)">
                            <b-icon
                                pack="mdi"
                                icon="format-list-numbered"
                                size="is-small" />
                            <span>Runs</span>
                        </b-button>
                    </div>
                </b-table-column>
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <p>No schedules of interest!</p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
        <div class="block">
            <h4 class="title is-4">
                Schedules with significant change in running duration
            </h4>

            <b-table
                :data="schedulesDelta"
                :striped="true"
                :narrowed="true"
                :loading="schedulesDeltaLoading"
                :hoverable="true">
                <b-table-column
                    v-slot="props"
                    field="description"
                    label="Schedule">
                    {{ props.row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="action"
                    label="Action">
                    {{ props.row.action }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="elapsedSecondsAverage"
                    label="Average duration">
                    {{ humanizeSeconds(props.row.elapsedSecondsAverage) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="elapsedSecondsLatest"
                    label="Latest duration">
                    {{ humanizeSeconds(props.row.elapsedSecondsLatest) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="delta"
                    label="Delta">
                    {{ props.row.delta }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    numeric
                    field="id"
                    label="Inspect">
                    <div class="buttons is-justify-content-end">
                        <b-button
                            type="is-primary"
                            size="is-small"
                            @click="runSchedule(props.row.scheduleId)">
                            <b-icon
                                pack="mdi"
                                icon="play"
                                size="is-small"
                                aria-hidden="true" />
                            <span>Run NOW!</span>
                        </b-button>
                        <b-button
                            type="is-info"
                            size="is-small"
                            @click="openRuns(props.row.scheduleId)">
                            <b-icon
                                pack="mdi"
                                icon="format-list-numbered"
                                size="is-small" />
                            <span>Runs</span>
                        </b-button>
                    </div>
                </b-table-column>
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <p>No schedules with significant delta change :)</p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
        <div class="block">
            <h4 class="title is-4">
                Schedules possibly running late
            </h4>

            <b-table
                :data="lateSchedules"
                :striped="true"
                :narrowed="true"
                :loading="lateSchedulesLoading"
                :hoverable="true">
                <b-table-column
                    v-slot="props"
                    field="description"
                    label="Schedule">
                    {{ props.row.description }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="action"
                    label="Action">
                    {{ props.row.action }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="scheduleSpec"
                    label="Cron">
                    {{ props.row.scheduleSpec }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="latestDate"
                    label="Last run">
                    {{ longDate(props.row.latestDate) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="expectedDate"
                    label="Expected date">
                    {{ longDate(props.row.expectedDate) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    numeric
                    field="id"
                    label="Inspect">
                    <div class="buttons is-justify-content-end">
                        <b-button
                            type="is-primary"
                            size="is-small"
                            @click="runSchedule(props.row.id)">
                            <b-icon
                                pack="mdi"
                                icon="play"
                                size="is-small"
                                aria-hidden="true" />
                            <span>Run NOW!</span>
                        </b-button>
                        <b-button
                            type="is-info"
                            size="is-small"
                            @click="openRuns(props.row.id)">
                            <b-icon
                                pack="mdi"
                                icon="format-list-numbered"
                                size="is-small" />
                            <span>Runs</span>
                        </b-button>
                    </div>
                </b-table-column>
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <p>No schedules running late :)</p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
        <div class="block">
            <h4 class="title is-4">
                Jobs (running/failed)
            </h4>
            <b-table
                :data="jobs"
                :striped="true"
                :narrowed="true"
                :loading="jobsLoading"
                :hoverable="true">
                <b-table-column
                    v-slot="props"
                    field="jobDateUpdated"
                    label="Timestamp">
                    {{ longDate(props.row.jobDateUpdated) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="jobType"
                    label="Type">
                    {{ props.row.jobType }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="jobState"
                    label="State">
                    {{ props.row.jobState }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="retryCount"
                    label="Retries">
                    {{ props.row.retryCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="releaseState"
                    label="Release state">
                    <router-link
                        :to="getReleaseLink(props.row.releaseId)">
                        {{ props.row.releaseState }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="releaseUserName"
                    label="Released by">
                    {{ props.row.releaseUserName }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="releaseUserPrincipal"
                    label="User Principal">
                    {{ props.row.releaseUserPrincipal }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="codeSet"
                    label="CodeSet">
                    <a
                        v-if="props.row.codeSet"
                        :href="getCodeSetLink(props.row.codeSet)">
                        {{ props.row.codeSet }}
                    </a>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="application"
                    label="Subscription">
                    <router-link
                        v-if="props.row.application"
                        :to="getSubscriptionLink(props.row.application, props.row.subscriptionId)">
                        {{ props.row.application }}, {{ props.row.subscriptionId }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    centered
                    field="id"
                    label="Halt & archive">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="archiveJob(props.row.jobId)">
                        <b-icon
                            pack="mdi"
                            icon="hand-back-right-outline"
                            size="is-small" />
                    </b-button>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    numeric
                    field="id"
                    label="Rerun">
                    <b-button
                        type="is-primary"
                        size="is-small"
                        @click="rerunJob(props.row.jobId)">
                        <b-icon
                            pack="mdi"
                            icon="play"
                            size="is-small"
                            aria-hidden="true" />
                    </b-button>
                </b-table-column>
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <p>No jobs are having issues...</p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
        <div class="block">
            <h4 class="title is-4">
                Releases (not yet completed)
            </h4>
            <b-table
                :data="releases"
                :striped="true"
                :narrowed="true"
                :loading="jobsLoading"
                :hoverable="true">
                <b-table-column
                    v-slot="props"
                    field="dateCreated"
                    label="Created">
                    {{ longDate(props.row.dateCreated) }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="state"
                    label="State">
                    <router-link
                        :to="getReleaseLink(props.row.id)">
                        {{ props.row.state }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="specificationType"
                    label="Type">
                    {{ props.row.specificationType }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="errorCount"
                    label="Errors">
                    {{ props.row.errorCount }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="library"
                    label="Library">
                    <router-link
                        v-if="props.row.library"
                        :to="getLibraryLink(props.row.library)">
                        {{ props.row.library }}
                    </router-link>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="codeSetName"
                    label="CodeSet">
                    <a
                        v-if="props.row.codeSetName"
                        :href="getCodeSetLink(props.row.codeSetName)">
                        {{ props.row.codeSetDescription }}
                    </a>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="userName"
                    label="Released by">
                    {{ props.row.userName }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="userPrincipal"
                    label="User Principal">
                    {{ props.row.userPrincipal }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    label="Action"
                    :visible="showReleaseActionColumn">
                    <b-button
                        v-if="props.row.state === 'Publishing'"
                        :loading="props.row.isAborting"
                        @click="abortPublishing(props.row)">
                        Abort publishing
                    </b-button>
                    <b-button
                        v-if="props.row.state === 'Open'"
                        :loading="props.row.isDiscarding"
                        @click="discardReleaseAction(props.row)">
                        Discard
                    </b-button>
                </b-table-column>
                <b-table-column
                    v-slot="props">
                    {{ props.row.abortPublishingError }}
                </b-table-column>
                <template #empty>
                    <section class="section">
                        <div class="content has-text-grey has-text-centered">
                            <p>All releases have been completed :D</p>
                        </div>
                    </section>
                </template>
            </b-table>
        </div>
    </div>
</template>

<style scoped>
.cursorPointer {
    cursor: pointer;
}
</style>
